import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Card,
	DraggableCard,
	FlexGap,
	Heading3,
	IconButton,
	Join,
	Paragraph,
	Pill,
	Radio,
	ReorderRenderState,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { useModalLink } from '../navigation/ModalNavigation';
import { GoodHabitzPill } from './GoodHabitzLogo';
import { goodHabitzCourseCount } from './util/goodHabitzCourseCount';

function GoalOwner(props: { owner: graphql.GoalOwner }) {
	const { t } = useTranslation();

	if (props.owner.__typename === 'User') {
		return (
			<Flex mt={`${tokens.gap.inline}px`} alignItems="center">
				<Avatar user={props.owner} size={24} mr={2} />
				<Paragraph flexGrow={1} flexShrink={1}>
					{props.owner.firstName} {props.owner.lastName}
				</Paragraph>
			</Flex>
		);
	}

	if (props.owner.__typename === 'Group') {
		return (
			<Flex mt={`${tokens.gap.inline}px`} alignItems="center">
				<Avatar.Group group={props.owner} size={24} mr={2} />
				<Paragraph flexGrow={1} flexShrink={1}>
					{props.owner.name}
				</Paragraph>
			</Flex>
		);
	}

	return (
		<Flex mt={`${tokens.gap.inline}px`} alignItems="center">
			<Avatar.Group size={24} mr={2} />
			<Paragraph flexGrow={1} flexShrink={1}>
				{t('goals.card.owner.organization')}
			</Paragraph>
		</Flex>
	);
}

function GoalDates(props: { goal: graphql.Goal | graphql.GoalRevision; isDue: boolean }) {
	const { t } = useTranslation();
	const { goal, isDue } = props;

	if (!goal.dueDate && !goal.completedAt && !goal.archivedAt && !goal.createdAt) {
		return null;
	}

	return (
		<Paragraph mt="4px">
			<Join separator=" · ">
				{goal.dueDate && (
					<span style={{ color: isDue ? tokens.error.text : undefined }}>
						{t('goals.card.dueDate', {
							date: moment(goal.dueDate).format('L'),
						})}
					</span>
				)}
				{goal.completedAt ? (
					<span>
						{t('goals.card.completedAt', {
							date: moment(goal.completedAt).format('L'),
						})}
					</span>
				) : (
					goal.createdAt && (
						<span>
							{t('goals.card.createdAt', {
								date: moment(goal.createdAt).format('L'),
							})}
						</span>
					)
				)}
				{goal.archivedAt && (
					<span>
						{t('goals.card.archivedAt', {
							date: moment(goal.archivedAt).format('L'),
						})}
					</span>
				)}
			</Join>
		</Paragraph>
	);
}

const GoalCard = Skeleton(
	function GoalCard(props: {
		user?: graphql.User;
		goal: graphql.Goal | graphql.GoalRevision;
		modal?: boolean;
		small?: boolean;
		compact?: boolean;
		readonly?: boolean;
		selected?: boolean;
		owner?: boolean;
		onClick?: () => void;
		onSelect?: () => void;
		dragHandleProps?: ReorderRenderState['dragHandleProps'];
		isDragging?: boolean;
		loading?: boolean;
		draggable?: boolean;
	}) {
		const { localize } = useLocalize();

		const isDue =
			moment(props.goal.dueDate).isBefore(moment()) &&
			props.goal.status === graphql.GoalStatus.Active;

		let regularLink =
			props.goal.__typename === 'GoalRevision'
				? `/goals/${props.goal.goalID}/revisions/${props.goal.revision}`
				: props.user
					? `/users/${props.user.id}/goals/${props.goal.id}`
					: `/goals/${props.goal.id}`;

		if (props.goal.__typename === 'GoalRevision' && props.goal.secret) {
			regularLink += `/${props.goal.secret}`;
		}

		const link = useModalLink(regularLink, props.modal ?? false);
		const returnURL = useLocation().pathname.replace('/v2', '');
		const compact = !!props.compact;
		const tight = props.small || compact;
		const showDates =
			!props.small &&
			!!(
				props.goal.dueDate ||
				props.goal.completedAt ||
				props.goal.archivedAt ||
				props.goal.createdAt
			);
		const showLabel = !!props.goal.label;
		const title = localize(props.goal.title).text;
		const owner = props.owner !== false && 'owner' in props.goal ? props.goal.owner : undefined;
		const interactive = !(props.onClick || props.onSelect || props.readonly) && !!props.goal.id;
		const archived = !!props.goal.isArchived;
		const goodHabitzCount = goodHabitzCourseCount(props.goal);

		const body = (
			<Flex alignItems="center" flexGrow={1} flexShrink={1} minWidth={0}>
				{props.onSelect && (
					<Box flexGrow={0} flexShrink={0} mr={3}>
						<Radio onChange={() => props.onSelect?.()} checked={props.selected} />
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1} minWidth={0}>
					<Heading3>{title}</Heading3>
					{showDates && <GoalDates goal={props.goal} isDue={isDue} />}
					{(showLabel || goodHabitzCount > 0) && (
						<FlexGap alignItems="center" gap={tokens.gap.inline} mt={`${tokens.gap.inline}px`}>
							{showLabel && <Pill small>{localize(props.goal.label!.title).text}</Pill>}
							{goodHabitzCount > 0 && <GoodHabitzPill count={goodHabitzCount} />}
						</FlexGap>
					)}
					{owner && <GoalOwner owner={owner} />}
				</Box>
			</Flex>
		);

		if (tight) {
			return (
				<Card
					link={interactive ? link : undefined}
					navigationState={{ returnURL }}
					onClick={props.onClick ?? props.onSelect}
					selected={props.selected}
					p={`${tokens.padding.control}px`}
					opacity={archived ? 0.5 : 1}
					chevron={false}
				>
					{body}
				</Card>
			);
		}

		if (props.draggable) {
			return (
				<Box style={{ opacity: archived ? 0.5 : 1 }}>
					<DraggableCard
						dragHandleProps={props.dragHandleProps ?? undefined}
						isDragging={props.isDragging}
						loading={props.loading}
						to={interactive ? link : undefined}
						toState={{ returnURL }}
						onClick={props.onClick ?? props.onSelect}
						selected={props.selected}
					>
						{body}
						{!props.onSelect && (
							<Box flexShrink={0} style={{ pointerEvents: 'none' }}>
								<IconButton type="secondary" icon={<ChevronRightIcon />} />
							</Box>
						)}
					</DraggableCard>
				</Box>
			);
		}

		return (
			<Box style={{ opacity: archived ? 0.5 : 1 }}>
				<Card
					link={interactive ? link : undefined}
					navigationState={{ returnURL }}
					onClick={props.onClick ?? props.onSelect}
					selected={props.selected}
					p={`${tokens.padding.container}px`}
					chevron={!props.onSelect && (interactive || !!props.onClick)}
				>
					{body}
				</Card>
			</Box>
		);
	},
	function GoalCardSkeleton(props: { small?: boolean; compact?: boolean }) {
		return (
			<Card.Skeleton
				p={`${props.small || props.compact ? tokens.padding.control : tokens.padding.container}px`}
			>
				<Heading3.Skeleton />
				<Paragraph.Skeleton mt="4px" lines={1} />
			</Card.Skeleton>
		);
	},
);

export default GoalCard;
